import type { Accessorials } from "@/Shared/types";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import LocationsDetails from "@/Shared/Locations/CustomComponents/LocationsDetails";
import type { PostalCodeChangeEvent } from "@/Shared/Locations/Inputs/LocationsPostalCodeInput";
import { removeUnavailableAccessorialsForLocation } from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LocationErrors } from "@/Shared/Locations/Validators/locationsErrorMessagesForLocation";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { useState } from "react";

interface NewLocationDetailsProps {
  locationErrorMessages: LocationErrors;
  locationWarningMessages: LocationErrors;
  context: LocationContext;
  setLocation: (_: AddLocationLocationType) => void;
  location: AddLocationLocationType;
  forceValidation: boolean;
  disablePostalCodeEntry: boolean;
  pickupDate: string;
}

function LocationsNewLocationDetails(props: NewLocationDetailsProps) {
  const { setLocation, location } = props;
  const address = location.address;
  const [distributionWarehouseName, setDistributionWarehouseName] = useState<
    string | undefined
  >(undefined);
  const accessorials = location.accessorials;

  const showMap =
    location.latitudeLongitude !== undefined ||
    location.latitudeLongitude !== undefined;

  function removeItemsStartingWith(values: string[], prefix: string) {
    return values.filter((v) => !v.startsWith(prefix));
  }

  function onPostalCodeChange(
    e: PostalCodeChangeEvent,
    _distributionWarehouseName: string | undefined,
  ) {
    const newLocation = {
      ...location,
      ...{ address: e.address, latitudeLongitude: e.latitudeLongitude },
    };

    setLocation(newLocation);
    setDistributionWarehouseName(_distributionWarehouseName);
  }

  function setSchedulingAccessorials(schedulingAccessorials: Accessorials) {
    const updatedAccessorials = removeItemsStartingWith(
      accessorials,
      "SCHEDULING_",
    );
    updatedAccessorials.push(...schedulingAccessorials);

    setLocation({
      ...location,
      ...{ accessorials: updatedAccessorials },
    });
  }

  function setLocationType(locationType: LocationType) {
    const newLocation = {
      ...location,
      ...{ locationType },
      // Switching to residential takes inside pickup/delivery away, so a box
      // ticked under the old type can't be left behind ticked and greyed out.
      accessorials: removeUnavailableAccessorialsForLocation(
        locationType,
        accessorials,
      ),
    };

    setLocation(newLocation);
  }

  function setDistributionWarehouseBrand(
    distributionWarehouseBrand: string | undefined,
  ) {
    const newLocation = {
      ...location,
      ...{ distributionWarehouseBrand },
    };

    setLocation(newLocation);
  }

  function setLogisticsAccessorials(logisticsAccessorials: Accessorials) {
    const updatedAccessorials = removeItemsStartingWith(
      accessorials,
      "LOGISTICS_",
    );
    updatedAccessorials.push(...logisticsAccessorials);
    const update = {
      ...location,
      ...{ accessorials: updatedAccessorials },
    };
    setLocation(update);
  }

  return (
    <Panel collapseOnNarrow>
      <LocationsDetails
        locationErrorMessages={props.locationErrorMessages}
        locationWarningMessages={props.locationWarningMessages}
        forceValidation={props.forceValidation}
        address={address}
        location={location}
        onPostalCodeChange={onPostalCodeChange}
        showMap={showMap}
        locationContext={props.context}
        latitudeLongitude={location.latitudeLongitude}
        setLocationType={setLocationType}
        setDistributionWarehouseBrand={setDistributionWarehouseBrand}
        accessorials={accessorials}
        setSchedulingAccessorials={setSchedulingAccessorials}
        setLogisticsAccessorials={setLogisticsAccessorials}
        disablePostalCodeEntry={props.disablePostalCodeEntry}
        distributionWarehouseName={distributionWarehouseName}
      />
    </Panel>
  );
}
export default LocationsNewLocationDetails;
